import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["value", "inputRef", "onChange", "defaultDate", "defaultTime"];
import I18n from 'I18n';
import { useMemo, useRef } from 'react';
// @ts-expect-error Legacy code isn't typed.
import * as SimpleDate from 'UIComponents/core/SimpleDate';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import UITimeInput from 'UIComponents/input/UITimeInput';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIInputGroup from 'UIComponents/form/UIInputGroup';
import UIBox from 'UIComponents/layout/UIBox';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MINUTES_IN_HOUR = 60;
const DEFAULT_TIME_MINUTES = 8 * 60; // 8 AM

export default function CRMPropertyDateTimeInput(_ref) {
  var _rest$property$label, _rest$property, _rest$property$label2, _rest$property2;
  let {
      value,
      inputRef: dateInputRef,
      onChange,
      defaultDate,
      defaultTime
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const timeInputRef = useRef(null);
  const momentValue = useMemo(() => {
    if (value || value === 0) {
      const timestamp = Number(value);
      if (isNaN(timestamp) || timestamp === 0) {
        return null;
      }
      return I18n.moment.userTz(timestamp);
    } else {
      return null;
    }
  }, [value]);
  const timeValue = momentValue && momentValue.hours() * MINUTES_IN_HOUR + momentValue.minutes();

  // If available use the selected "date" input value
  // instead of the current date to account for
  // daylight saving time changes.
  const timezoneLabel = momentValue ? momentValue.format('z') : I18n.moment.userTz().format('z');
  const timezoneFormat = `LT [${timezoneLabel}]`;
  const dateValue = momentValue && SimpleDate.fromMoment(momentValue);
  const handleDateChange = evt => {
    const date = evt.target.value; // 'as unknown as SimpleDate | null' is required because UIDateInput types onChange as `React.ChangeEvent<HTMLInputElement>` but actually broadcasts a `SyntheticEventType<SimpleDate | null>`

    // If the date is null, we should clear both the date and time values
    if (date === null) {
      if (typeof onChange === 'function') {
        onChange(SyntheticEvent(null));
      }
      return;
    }
    const dateMoment = SimpleDate.toMoment(date || defaultDate || SimpleDate.now());
    const minutes = timeValue !== null ? timeValue : defaultTime || DEFAULT_TIME_MINUTES;
    const timestamp = dateMoment.add(minutes, 'minutes').valueOf();
    const focusDateInput = timeValue === null && dateValue === null;
    const isValueChanged = (value === null || value === void 0 ? void 0 : value.toString()) !== timestamp.toString();
    if (typeof onChange === 'function' && isValueChanged) {
      onChange(SyntheticEvent(timestamp));
    }
    if (focusDateInput) {
      var _timeInputRef$current;
      timeInputRef === null || timeInputRef === void 0 || (_timeInputRef$current = timeInputRef.current) === null || _timeInputRef$current === void 0 || _timeInputRef$current.focus();
    }
  };
  const handleTimeChange = evt => {
    const time = evt.target.value;
    const dateMoment = SimpleDate.toMoment(dateValue || defaultDate || SimpleDate.now());
    const minutes = time !== null ? time : defaultTime || DEFAULT_TIME_MINUTES;
    const timestamp = dateMoment.add(minutes, 'minutes').valueOf();
    const focusDateInput = timeValue === null && dateValue === null;
    const isValueChanged = (value === null || value === void 0 ? void 0 : value.toString()) !== timestamp.toString();
    if (typeof onChange === 'function' && isValueChanged) {
      onChange(SyntheticEvent(timestamp));
    }
    if (focusDateInput) {
      var _dateInputRef$current;
      dateInputRef === null || dateInputRef === void 0 || (_dateInputRef$current = dateInputRef.current) === null || _dateInputRef$current === void 0 || _dateInputRef$current.focus();
    }
  };
  const {
    disabled,
    readOnly,
    error
  } = rest;
  return /*#__PURE__*/_jsx(UIInputGroup, {
    children: /*#__PURE__*/_jsxs(UIFlex, {
      gap: 8,
      children: [/*#__PURE__*/_jsx(UIBox, {
        grow: 1,
        children: /*#__PURE__*/_jsx(UIFormControl, {
          "aria-label": (_rest$property$label = rest === null || rest === void 0 || (_rest$property = rest.property) === null || _rest$property === void 0 ? void 0 : _rest$property.label) !== null && _rest$property$label !== void 0 ? _rest$property$label : '',
          children: /*#__PURE__*/_jsx(UIDateInput, {
            inputRef: dateInputRef
            // HACK: prevent ESC key presses from propagating up and cancelling changes
            // See https://git.hubteam.com/HubSpot/UIComponents/issues/9877
            ,
            onKeyDown: _stopPropagation,
            onKeyPress: _stopPropagation,
            onKeyUp: _stopPropagation,
            disabled: disabled,
            readOnly: readOnly,
            value: dateValue,
            onChange: handleDateChange
            // Currently, only validations with date are supported
            // When validations errors include time, we may want to show the error on the time input as well
            ,
            error: error
          })
        })
      }), /*#__PURE__*/_jsx(UIBox, {
        grow: 1,
        children: /*#__PURE__*/_jsx(UIFormControl, {
          "aria-label": (_rest$property$label2 = rest === null || rest === void 0 || (_rest$property2 = rest.property) === null || _rest$property2 === void 0 ? void 0 : _rest$property2.label) !== null && _rest$property$label2 !== void 0 ? _rest$property$label2 : '',
          children: /*#__PURE__*/_jsx(UITimeInput, {
            inputRef: timeInputRef,
            disabled: disabled,
            readOnly: readOnly
            // @ts-expect-error UITimeInput propTypes allow 'null', but TS types do not; see https://git.hubteam.com/HubSpot/UIComponents/issues/10822
            ,
            value: timeValue,
            format: timezoneFormat
            // We use this to remount the component if the the timezone label changes based on date
            ,

            onChange: handleTimeChange
          }, timezoneFormat)
        })
      })]
    })
  });
}
function _stopPropagation(e) {
  e.stopPropagation();
}